import { CopyOutlined } from '@ant-design/icons'
import { Alert, Button, Card, message, Modal, Space, Tag } from 'antd'
import dayjs from 'dayjs'
import { DaoPoolType } from 'Modules'
import { formatUnits } from 'viem'
import { useAccount } from 'wagmi'

import ButtonCopy from 'component/button/button-copy'
import {
  DAO_DATETIME_FORMAT,
  DAO_POOL_API_TYPE_LABEL,
  DAO_POOL_STATUS_CONFIG,
} from 'constants/dao-voting'
import { daoTxErrorMessage } from 'hooks/dao-voting/use-prepared-tx'
import { useRedemptionPoolFunding } from 'hooks/dao-voting/use-redemption-pool-funding'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

const AddressBox = ({ label, address }: { label: string; address?: string | null }) => (
  <div className="dao-pool-address-box">
    <div className="label">{label}</div>
    {address ? (
      <Space>
        <span className="value">{address}</span>
        <ButtonCopy title="Copy" text={address} icon={<CopyOutlined />} />
      </Space>
    ) : (
      <span className="value">Not deployed</span>
    )}
  </div>
)

/**
 * PTO ERC-3643 only: the pool is deployed unfunded, so claims revert until the
 * reward token is sent to it (DAO_GOVERNANCE_ERC3643_FE_NOTE 6.3). Read on-chain,
 * so it stays right after a reload or a partial top-up.
 */
const PoolFunding = ({ poolAddress, canManage }: { poolAddress: string; canManage: boolean }) => {
  const { address } = useAccount()
  const { funding, error, refetch, fundPool } = useRedemptionPoolFunding(poolAddress)

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginTop: 16 }}
        message="Cannot read the pool funding on-chain"
        action={
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    )
  }
  if (!funding || funding.isEnded) return null

  const fmt = (value: bigint) =>
    `${Number(formatUnits(value, funding.decimals)).toLocaleString()} ${funding.symbol}`

  if (funding.shortfall === 0n) {
    return (
      <Alert
        type="success"
        showIcon
        style={{ marginTop: 16 }}
        message={`Pool is funded - ${fmt(funding.balance)} available for claims`}
      />
    )
  }

  const handleFund = () => {
    if (!address) {
      message.error('Please connect wallet!')
      return
    }
    Modal.confirm({
      title: 'Fund Pool',
      content: `Transfer ${fmt(funding.shortfall)} from the connected wallet to the pool?`,
      okText: 'Fund Pool',
      onOk: async () => {
        try {
          onOpenModalProcessing()
          await fundPool(address, funding)
          message.success('Fund pool successfully')
          refetch()
        } catch (fundError) {
          logError('Error funding pool: ', fundError)
          message.error(daoTxErrorMessage(fundError) || 'Failed to fund pool')
        } finally {
          onCloseModalProcessing()
        }
      },
    })
  }

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginTop: 16 }}
      message="Pool is not fully funded - holders cannot claim until it is"
      description={`Holds ${fmt(funding.balance)} of the ${fmt(funding.needed)} still claimable.`}
      action={
        canManage ? (
          <Button size="small" type="primary" onClick={handleFund}>
            Fund {fmt(funding.shortfall)}
          </Button>
        ) : undefined
      }
    />
  )
}

interface PoolCardProps {
  pool: DaoPoolType
  isErc3643?: boolean
  canManage?: boolean
}

/** Pool card on PTO DAO Detail (SRS 5.2 Pool Detail) - shown once a pool exists */
const PoolCard = ({ pool, isErc3643 = false, canManage = false }: PoolCardProps) => {
  const status = DAO_POOL_STATUS_CONFIG[pool.poolStatus]
  const unit = isErc3643 ? 'PTO' : 'NFT'

  return (
    <Card className="dao-pool-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>
          {DAO_POOL_API_TYPE_LABEL[pool.poolType] || pool.poolType}
        </span>
        {status ? <Tag color={status.color}>{status.label}</Tag> : <Tag>{pool.poolStatus}</Tag>}
      </div>

      <AddressBox label="Pool Contract Address" address={pool.contractAddress} />
      <AddressBox label="Owner Address" address={pool.ownerAddress} />

      {isErc3643 && pool.contractAddress ? (
        <PoolFunding poolAddress={pool.contractAddress} canManage={canManage} />
      ) : null}

      <div className="dao-pool-grid">
        <div className="dao-pool-item">
          <div className="label">Amount per {unit}</div>
          <div className="value">${pool.amountPerNft.toLocaleString()}</div>
        </div>
        <div className="dao-pool-item">
          <div className="label">Total Funded</div>
          <div className="value">${pool.totalFunded.toLocaleString()}</div>
        </div>
        <div className="dao-pool-item">
          <div className="label">Claimed / Unclaimed</div>
          <div className="value">
            {pool.claimedCount} / {pool.unclaimedCount}
          </div>
        </div>
        <div className="dao-pool-item">
          <div className="label">Claims Open</div>
          <div className="value">
            {pool.claimOpenAt ? dayjs(pool.claimOpenAt).format(DAO_DATETIME_FORMAT) : '--'}
          </div>
        </div>
        <div className="dao-pool-item">
          <div className="label">Claim Deadline</div>
          <div className="value">
            {pool.claimDeadline
              ? dayjs(pool.claimDeadline).format(DAO_DATETIME_FORMAT)
              : 'No deadline'}
          </div>
        </div>
        <div className="dao-pool-item">
          <div className="label">Remaining Balance</div>
          <div className="value">${pool.remainingBalance.toLocaleString()}</div>
        </div>
      </div>
    </Card>
  )
}

export default PoolCard
